import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Building2,
  Save,
  Trash2,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Users,
  DollarSign,
  Settings,
  BarChart3,
  Eye,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'
import { Textarea } from '@/components/ui/textarea'
import StartImpersonationDialog from '@/components/StartImpersonationDialog'

interface ChurchData {
  id?: string
  name: string
  email: string
  phone: string
  website: string
  address: string
  city: string
  state: string
  country: string
  postal_code: string
  timezone: string
}

interface SubscriptionData {
  plan: 'starter' | 'growth' | 'leadership'
  status: 'trial' | 'active' | 'past_due' | 'cancelled' | 'suspended'
  monthly_price: number
  max_members?: number
  max_admins?: number
  trial_ends_at?: string
}

interface ChurchManagementProps {
  user: AdminUser | null
}

export default function ChurchManagement({ user }: ChurchManagementProps) {
  const navigate = useNavigate()
  const { churchId } = useParams()
  const isNew = churchId === 'new'

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showImpersonateDialog, setShowImpersonateDialog] = useState(false)

  const [churchData, setChurchData] = useState<ChurchData>({
    name: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    state: '',
    country: 'United States',
    postal_code: '',
    timezone: 'America/New_York',
  })

  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData>({
    plan: 'trial' as any,
    status: 'trial',
    monthly_price: 0,
  })

  const [stats, setStats] = useState({
    total_members: 0,
    active_members: 0,
    total_admins: 0,
  })

  // Check if user is super admin
  if (user?.adminLevel !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You do not have permission to manage churches.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  useEffect(() => {
    if (!isNew && churchId) {
      loadChurchData()
    }
  }, [churchId, isNew])

  async function loadChurchData() {
    try {
      setLoading(true)

      // Load church data
      const { data: church, error: churchError } = await supabase
        .from('churches')
        .select('*')
        .eq('id', churchId)
        .single()

      if (churchError) throw churchError

      setChurchData(church)

      // Load subscription data
      const { data: subscription } = await supabase
        .from('church_subscriptions')
        .select('*')
        .eq('church_id', churchId)
        .single()

      if (subscription) {
        setSubscriptionData(subscription)
      }

      // Load member stats
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, role, admin_level, is_active')
        .eq('church_id', churchId)

      const totalMembers = profiles?.length || 0
      const activeMembers = profiles?.filter((p) => p.is_active)?.length || 0
      const totalAdmins = profiles?.filter((p) => p.role === 'admin')?.length || 0

      setStats({
        total_members: totalMembers,
        active_members: activeMembers,
        total_admins: totalAdmins,
      })
    } catch (error) {
      console.error('Error loading church data:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleSave() {
    try {
      setSaving(true)

      if (isNew) {
        // Create new church
        const { data: newChurch, error: churchError } = await supabase
          .from('churches')
          .insert(churchData)
          .select()
          .single()

        if (churchError) throw churchError

        // Create subscription
        const { error: subscriptionError } = await supabase
          .from('church_subscriptions')
          .insert({
            church_id: newChurch.id,
            ...subscriptionData,
          })

        if (subscriptionError) throw subscriptionError

        // Create onboarding record
        const { error: onboardingError } = await supabase
          .from('church_onboarding')
          .insert({
            church_id: newChurch.id,
            status: 'not_started',
          })

        if (onboardingError) throw onboardingError

        // Log audit
        await supabase.from('platform_audit_log').insert({
          admin_id: user!.id,
          admin_email: user!.email,
          admin_name: `${user!.firstName} ${user!.lastName}`,
          action: 'church_created',
          resource_type: 'church',
          resource_id: newChurch.id,
          church_id: newChurch.id,
          new_values: churchData,
          description: `Created new church: ${churchData.name}`,
        })

        alert('Church created successfully!')
        navigate(`/platform/churches/${newChurch.id}`)
      } else {
        // Update existing church
        const { error: churchError } = await supabase
          .from('churches')
          .update(churchData)
          .eq('id', churchId)

        if (churchError) throw churchError

        // Update subscription
        const { error: subscriptionError } = await supabase
          .from('church_subscriptions')
          .update(subscriptionData)
          .eq('church_id', churchId)

        if (subscriptionError) throw subscriptionError

        // Log audit
        await supabase.from('platform_audit_log').insert({
          admin_id: user!.id,
          admin_email: user!.email,
          admin_name: `${user!.firstName} ${user!.lastName}`,
          action: 'church_updated',
          resource_type: 'church',
          resource_id: churchId,
          church_id: churchId,
          new_values: churchData,
          description: `Updated church: ${churchData.name}`,
        })

        alert('Church updated successfully!')
      }
    } catch (error: any) {
      console.error('Error saving church:', error)
      alert('Failed to save church: ' + error.message)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    try {
      setDeleting(true)

      // Log audit before deletion
      await supabase.from('platform_audit_log').insert({
        admin_id: user!.id,
        admin_email: user!.email,
        admin_name: `${user!.firstName} ${user!.lastName}`,
        action: 'church_deleted',
        resource_type: 'church',
        resource_id: churchId,
        church_id: churchId,
        old_values: churchData,
        description: `Deleted church: ${churchData.name}`,
      })

      // Delete church (cascade will handle related records)
      const { error } = await supabase.from('churches').delete().eq('id', churchId)

      if (error) throw error

      alert('Church deleted successfully!')
      navigate('/platform')
    } catch (error: any) {
      console.error('Error deleting church:', error)
      alert('Failed to delete church: ' + error.message)
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading church data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/platform')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Building2 className="h-8 w-8" />
              {isNew ? 'Add New Church' : churchData.name}
            </h1>
            <p className="text-muted-foreground">
              {isNew ? 'Create a new church account' : 'Manage church settings and subscription'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <>
              <Button
                variant="outline"
                onClick={() => setShowImpersonateDialog(true)}
              >
                <Eye className="h-4 w-4 mr-2" />
                Impersonate Church
              </Button>
              <StartImpersonationDialog
                open={showImpersonateDialog}
                onOpenChange={setShowImpersonateDialog}
                churchId={churchId!}
                churchName={churchData.name}
              />
            </>
          )}
          {!isNew && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={deleting}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Church
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete the church
                    "{churchData.name}" and all associated data including members, contributions,
                    events, and more.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-red-600">
                    {deleting ? 'Deleting...' : 'Delete Church'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {isNew ? 'Create Church' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Stats Cards (only for existing churches) */}
      {!isNew && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Members</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_members}</div>
              <p className="text-xs text-muted-foreground">{stats.active_members} active</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Administrators</CardTitle>
              <Settings className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_admins}</div>
              <p className="text-xs text-muted-foreground">Church admins</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                GHS {subscriptionData.monthly_price.toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground capitalize">
                {subscriptionData.plan} plan
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">Church Details</TabsTrigger>
          <TabsTrigger value="subscription">Subscription</TabsTrigger>
          {!isNew && <TabsTrigger value="analytics">Analytics</TabsTrigger>}
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Core church details and contact information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Church Name *</Label>
                  <Input
                    id="name"
                    value={churchData.name}
                    onChange={(e) => setChurchData({ ...churchData, name: e.target.value })}
                    placeholder="First Baptist Church"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={churchData.email}
                    onChange={(e) => setChurchData({ ...churchData, email: e.target.value })}
                    placeholder="contact@church.org"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={churchData.phone}
                    onChange={(e) => setChurchData({ ...churchData, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={churchData.website}
                    onChange={(e) => setChurchData({ ...churchData, website: e.target.value })}
                    placeholder="https://church.org"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Street Address</Label>
                <Input
                  id="address"
                  value={churchData.address}
                  onChange={(e) => setChurchData({ ...churchData, address: e.target.value })}
                  placeholder="123 Main Street"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={churchData.city}
                    onChange={(e) => setChurchData({ ...churchData, city: e.target.value })}
                    placeholder="New York"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state">State/Province</Label>
                  <Input
                    id="state"
                    value={churchData.state}
                    onChange={(e) => setChurchData({ ...churchData, state: e.target.value })}
                    placeholder="NY"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postal_code">Postal Code</Label>
                  <Input
                    id="postal_code"
                    value={churchData.postal_code}
                    onChange={(e) => setChurchData({ ...churchData, postal_code: e.target.value })}
                    placeholder="10001"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={churchData.country}
                    onChange={(e) => setChurchData({ ...churchData, country: e.target.value })}
                    placeholder="United States"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Select
                  value={churchData.timezone}
                  onValueChange={(value) => setChurchData({ ...churchData, timezone: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                    <SelectItem value="America/Anchorage">Alaska Time</SelectItem>
                    <SelectItem value="Pacific/Honolulu">Hawaii Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subscription" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Subscription Management</CardTitle>
              <CardDescription>Configure subscription plan and billing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="plan">Subscription Plan</Label>
                  <Select
                    value={subscriptionData.plan}
                    onValueChange={(value: any) =>
                      setSubscriptionData({ ...subscriptionData, plan: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">Starter</SelectItem>
                      <SelectItem value="growth">Growth</SelectItem>
                      <SelectItem value="leadership">Leadership</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Subscription Status</Label>
                  <Select
                    value={subscriptionData.status}
                    onValueChange={(value: any) =>
                      setSubscriptionData({ ...subscriptionData, status: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="past_due">Past Due</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="monthly_price">Monthly Price (GHS)</Label>
                  <Input
                    id="monthly_price"
                    type="number"
                    step="0.01"
                    value={subscriptionData.monthly_price}
                    onChange={(e) =>
                      setSubscriptionData({
                        ...subscriptionData,
                        monthly_price: parseFloat(e.target.value),
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_members">Max Members (Optional)</Label>
                  <Input
                    id="max_members"
                    type="number"
                    value={subscriptionData.max_members || ''}
                    onChange={(e) =>
                      setSubscriptionData({
                        ...subscriptionData,
                        max_members: e.target.value ? parseInt(e.target.value) : undefined,
                      })
                    }
                    placeholder="Unlimited"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_admins">Max Admins (Optional)</Label>
                  <Input
                    id="max_admins"
                    type="number"
                    value={subscriptionData.max_admins || ''}
                    onChange={(e) =>
                      setSubscriptionData({
                        ...subscriptionData,
                        max_admins: e.target.value ? parseInt(e.target.value) : undefined,
                      })
                    }
                    placeholder="Unlimited"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="trial_ends_at">Trial End Date</Label>
                  <Input
                    id="trial_ends_at"
                    type="date"
                    value={subscriptionData.trial_ends_at?.split('T')[0] || ''}
                    onChange={(e) =>
                      setSubscriptionData({
                        ...subscriptionData,
                        trial_ends_at: e.target.value
                          ? new Date(e.target.value).toISOString()
                          : undefined,
                      })
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Church Analytics
              </CardTitle>
              <CardDescription>Usage statistics and growth metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-center py-8">
                Analytics dashboard coming soon...
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
